// Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { fetchChainTimestampDeltaTx } from '@react/chainThunks/fetchChainTimestampDeltaTx'
import { otAssert } from '@shared/conversions'
import { logOT } from '@shared/loggingShared'
import {
  WSCLientAction,
  WSClientActionAccountBalancesUpdate,
  WSClientActionClockUpdate,
  WSClientActionPoolAccountUpdate,
  WSClientActionPoolOverviewUpdate,
  WSClientActions,
} from '@shared/WsClientActions'
import { wsServerActionPoolOverviewSubscribe } from '@shared/WsServerActions'
import { chainActions } from './chainSlice'
import { poolActions } from './poolSlice'
import { OtDispatch } from './store'

export async function otDelay(time: number): Promise<void> {
  return await new Promise((resolve) => setTimeout(resolve, time))
}

export function wsPoolOverviewSpy(): number {
  return 1
}
export const wsOnMessage = (event: MessageEvent, dispatch: OtDispatch) => {
  const jsonStr = event.data
  const wsAction = JSON.parse(jsonStr) as WSCLientAction
  logOT.info('WebsocketProvider onmessage', wsAction.action, wsAction)

  switch (wsAction.action) {
    case WSClientActions.POOL_OVERVIEW_DATA:
      {
        const action = wsAction as WSClientActionPoolOverviewUpdate
        logOT.debug('WebsocketProvider onmessage', wsAction.action, action.poolOverviewCTO)
        dispatch(poolActions.updatePoolOverviewCTO(action.poolOverviewCTO))

        dispatch(poolActions.updatePoolOffchainDataCTO(action.poolOffchainDataCTO))
        wsPoolOverviewSpy()
      }
      break
    case WSClientActions.POOL_ACCOUNT_DATA:
      {
        const action = wsAction as WSClientActionPoolAccountUpdate

        logOT.debug(
          'WebsocketProvider onmessage',
          wsAction.action,
          action.poolAccountCTO.accountAddr,
          action.poolAccountCTO
        )
        dispatch(poolActions.updatePoolAccountCTO(action.poolAccountCTO))
      }
      break
    case WSClientActions.CHAIN_TIMESTAMP_DELTA:
      {
        logOT.debug('WebsocketProvider CHAIN_TIMESTAMP_DELTA', wsAction)
        dispatch(fetchChainTimestampDeltaTx())
      }
      break

    case WSClientActions.ACCOUNT_BALANCES_DATA:
      {
        logOT.debug('wsClient ACCOUNT_BALANCES_DATA', wsAction)
        const action = wsAction as WSClientActionAccountBalancesUpdate
        logOT.debug('wsClient CHAIN_TIMESTAMP_DELTA', wsAction)
        dispatch(chainActions.updateAccountBalances(action.accountBalances))
      }
      break
    case WSClientActions.CLOCK_DATA:
      {
        logOT.debug('wsClient CLOCK_DATA', wsAction)
        const action = wsAction as WSClientActionClockUpdate
        logOT.debug('wsClient CLOCK_DATA', wsAction)
        dispatch(
          chainActions.updateChainTimeDelta({
            chainTimestampDelta: action.chainTimestampDelta,
          })
        )
      }
      break
  }
}

export const createClientWebsocketHandler = async (
  websocketUrl: string,
  dispatch: OtDispatch,
  chainConfigurationName: string
): Promise<WebSocket> => {
  otAssert(websocketUrl, 'createClientWebsocketHandler websocketUrl')
  logOT.debug('createClientWebsocketHandler 1 websocketUrl ' + websocketUrl + ' ' + chainConfigurationName)

  const wsurl = process.env.JEST_WORKER_ID
    ? websocketUrl.replace('https://', 'wss://').replace('http://', 'ws://')
    : websocketUrl
  const webSocket = new WebSocket(wsurl)

  logOT.debug('createClientWebsocketHandler 2 websocketUrl', !!webSocket, websocketUrl)
  let isOpen = false
  webSocket.onopen = () => {
    logOT.debug('WebsocketProvider onOpen', new Date())
    isOpen = true

    logOT.debug(
      'wsServerActionChainConfigurationAssign ON OPEN send wsServerActionChainConfigurationAssign chainConfigurationName',
      chainConfigurationName
    )

    logOT.debug('wsServerActionPoolOverviewSubscribe chainConfigurationName', !!webSocket, chainConfigurationName)

    webSocket?.send(wsServerActionPoolOverviewSubscribe(chainConfigurationName))
  }
  logOT.debug('createClientWebsocketHandler 3 websocketUrl', websocketUrl)
  webSocket.onclose = () => {
    logOT.debug('WebsocketProvider onclose', new Date())
    dispatch(chainActions.wsDisconnect())
  }
  webSocket.onmessage = (event: MessageEvent) => {
    wsOnMessage(event, dispatch)
  }
  webSocket.onerror = (e) => {
    logOT.error('WebsocketProvider onerror', Object.keys(e))
    logOT.debug('WebsocketProvider onerror', e.isTrusted)
    if (!e.isTrusted) {
      logOT.error('WebsocketProvider onerror', e)
      dispatch(chainActions.wsDisconnect())
    }
  }
  while (!isOpen) {
    await otDelay(100)
  }

  logOT.debug('createClientWebsocketHandler 4 websocketUrl', !!dispatch, websocketUrl)
  dispatch(chainActions.wsConnect({ webSocket }))
  logOT.debug('createClientWebsocketHandler 5 websocketUrl', websocketUrl)

  return webSocket
}
